import React, { useRef, useState, useEffect } from 'react';
import { Service } from '../../types';
import { ClipboardSignature, FileImage, Loader2, X } from 'lucide-react';
import { apiService } from '../../services/api';
import QuoteRequestInstructionsPanel from './QuoteRequestInstructionsPanel';

export type QuoteRequestEntry = {
  details: string;
  attachmentIds?: string[];
  imageUrls?: string[];
  /** Blob URLs for thumbnail preview (from selected files); not sent to API */
  previewUrls?: string[];
};

/** Single key used for combined details and images (one column each in quotes table). */
export const QUOTE_REQUEST_ALL_KEY = '__all__';

const MAX_QUOTE_IMAGES = 10;

/** Matches getThemeStyles() / BookingStep1 — includes inline hex colors for branded booking */
interface ThemeStyles {
  pageBg?: string;
  cardBg?: string;
  textPrimary?: string;
  textSecondary?: string;
  border?: string;
  buttonPrimary?: string;
  inlineStyles?: {
    buttonPrimary?: { backgroundColor?: string };
    border?: { borderColor?: string };
    textPrimary?: { color?: string };
    textSecondary?: { color?: string };
    cardBg?: { backgroundColor?: string };
  };
}

interface BookingStepQuoteProps {
  quotableServices: Service[];
  quoteRequests: { [serviceId: string]: QuoteRequestEntry };
  onQuoteRequestsChange: (next: { [serviceId: string]: QuoteRequestEntry }) => void;
  preferredScheduleText?: string;
  onPreferredScheduleTextChange?: (value: string) => void;
  businessId: string;
  themeStyles?: ThemeStyles;
}

const BookingStepQuote: React.FC<BookingStepQuoteProps> = ({
  quotableServices,
  quoteRequests,
  onQuoteRequestsChange,
  preferredScheduleText = '',
  onPreferredScheduleTextChange,
  businessId,
  themeStyles
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxUrl(null);
    };
    if (lightboxUrl) {
      document.addEventListener('keydown', onEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [lightboxUrl]);

  const primaryColor =
    themeStyles?.inlineStyles?.buttonPrimary?.backgroundColor ?? '#3B82F6';
  const borderColor = themeStyles?.inlineStyles?.border?.borderColor ?? '#e5e7eb';
  const textColor = themeStyles?.inlineStyles?.textPrimary?.color ?? '#111827';
  const mutedColor = themeStyles?.inlineStyles?.textSecondary?.color ?? '#6b7280';
  const cardSurface =
    themeStyles?.inlineStyles?.cardBg?.backgroundColor ?? '#fff';

  const entry = quoteRequests[QUOTE_REQUEST_ALL_KEY] || { details: '', attachmentIds: [], imageUrls: [] };
  const imageCount = Math.max(entry.attachmentIds?.length ?? 0, entry.imageUrls?.length ?? 0);

  const updateDetails = (details: string) => {
    onQuoteRequestsChange({
      ...quoteRequests,
      [QUOTE_REQUEST_ALL_KEY]: {
        ...entry,
        details
      }
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const currentCount = imageCount;
    if (currentCount >= MAX_QUOTE_IMAGES) return;
    const fileList = Array.from(files).slice(0, MAX_QUOTE_IMAGES - currentCount);
    if (fileList.length === 0) return;
    const previewUrlsToAdd = fileList.map((file) => URL.createObjectURL(file));
    setUploading(true);
    try {
      const { attachmentIds, urls } = await apiService.uploadQuoteImages(businessId, fileList);
      onQuoteRequestsChange({
        ...quoteRequests,
        [QUOTE_REQUEST_ALL_KEY]: {
          ...entry,
          attachmentIds: [...(entry.attachmentIds || []), ...attachmentIds],
          imageUrls: [...(entry.imageUrls || []), ...urls],
          previewUrls: [...(entry.previewUrls || []), ...previewUrlsToAdd]
        }
      });
    } catch (err: any) {
      previewUrlsToAdd.forEach((url) => URL.revokeObjectURL(url));
      console.error('Quote image upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearImages = () => {
    (entry.previewUrls || []).forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    onQuoteRequestsChange({
      ...quoteRequests,
      [QUOTE_REQUEST_ALL_KEY]: { ...entry, attachmentIds: [], imageUrls: [], previewUrls: [] }
    });
  };

  const removeImageAtIndex = (index: number) => {
    const preview = entry.previewUrls || [];
    const urlToRevoke = preview[index];
    if (urlToRevoke?.startsWith('blob:')) URL.revokeObjectURL(urlToRevoke);
    const ids = entry.attachmentIds || [];
    const urls = entry.imageUrls || [];
    onQuoteRequestsChange({
      ...quoteRequests,
      [QUOTE_REQUEST_ALL_KEY]: {
        ...entry,
        attachmentIds: ids.filter((_, i) => i !== index),
        imageUrls: urls.filter((_, i) => i !== index),
        previewUrls: preview.filter((_, i) => i !== index)
      }
    });
  };

  const imageUrlsList = entry.imageUrls || [];
  const attachmentIdsList = entry.attachmentIds || [];
  const previewUrlsList = entry.previewUrls || [];
  const displayCount = Math.max(imageUrlsList.length, attachmentIdsList.length, previewUrlsList.length);
  const thumbnails = previewUrlsList.length >= displayCount
    ? previewUrlsList
    : imageUrlsList.length > 0
      ? imageUrlsList
      : attachmentIdsList.map(() => null);

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <ClipboardSignature className="w-4 h-4" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-xl text-gray-900">Quote Request</h2>
        </div>
        <p className="text-sm" style={{ color: mutedColor }}>
          Provide details for your request so we can prepare a quote. You can attach images if helpful.
        </p>
        {quotableServices.length > 0 && (
          <p className="text-sm mt-1" style={{ color: mutedColor }}>
            Services: {quotableServices.map((s) => s.name).join(', ')}
          </p>
        )}
      </div>

      <QuoteRequestInstructionsPanel
        services={quotableServices}
        brandColor={primaryColor}
        mutedColor={mutedColor}
        borderColor={borderColor}
      />

      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
          Details <span className="text-red-500 normal-case">*</span>
        </p>
        <div
          className="rounded-xl border p-5 space-y-4"
          style={{ borderColor, backgroundColor: cardSurface }}
        >
        <div>
          <label htmlFor="quote-request-details" className="sr-only">
            Details (required)
          </label>
          <p
            id="quote-request-details-hint"
            className="text-sm mb-2"
            style={{ color: mutedColor }}
          >
            Include your service address, job details, and any special requests to help us prepare your quote.
          </p>
          <textarea
            id="quote-request-details"
            required
            aria-required
            aria-describedby="quote-request-details-hint"
            value={entry.details}
            onChange={(e) => updateDetails(e.target.value)}
            rows={4}
            className="w-full rounded-lg border p-3 text-sm resize-y"
            style={{ borderColor, color: textColor }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: mutedColor }}>
            Images (optional)
          </label>
          <p className="text-xs mb-3" style={{ color: mutedColor }}>
            Add photos to help us understand your request (e.g. room size, condition, reference). Accepted: JPEG, PNG, GIF, or WebP. Max 50 MB per image. Up to {MAX_QUOTE_IMAGES} images.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || imageCount >= MAX_QUOTE_IMAGES}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed text-sm font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                borderColor: primaryColor,
                color: primaryColor,
                backgroundColor: `${primaryColor}08`
              }}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileImage className="w-4 h-4" />
              )}
              {uploading ? 'Uploading...' : imageCount >= MAX_QUOTE_IMAGES ? `Max ${MAX_QUOTE_IMAGES} images` : 'Add images'}
            </button>
            {imageCount >= MAX_QUOTE_IMAGES && (
              <span className="text-xs" style={{ color: mutedColor }}>
                Maximum reached
              </span>
            )}
            {imageCount > 0 && (
              <button
                type="button"
                onClick={clearImages}
                className="text-sm font-medium hover:underline"
                style={{ color: mutedColor }}
              >
                Clear all
              </button>
            )}
          </div>
          {displayCount > 0 && (
            <div className="mt-4 w-full">
              <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: mutedColor }}>
                {displayCount} image{displayCount !== 1 ? 's' : ''} attached
              </p>
              <div
                className="grid grid-cols-2 gap-2 w-full md:flex md:flex-wrap md:gap-3"
                style={{ maxWidth: '100%' }}
              >
                {thumbnails.map((url, index) => (
                  <div
                    key={`${index}-${url || 'pl'}`}
                    className="relative group rounded-lg overflow-hidden border shadow-sm w-full aspect-square min-w-0 md:w-[88px] md:h-[88px] md:aspect-auto md:flex-shrink-0 cursor-pointer"
                    style={{
                      borderColor: borderColor,
                      backgroundColor: `${borderColor}40`
                    }}
                    onClick={() => url && setLightboxUrl(url)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && url) {
                        e.preventDefault();
                        setLightboxUrl(url);
                      }
                    }}
                    aria-label={`View image ${index + 1}`}
                  >
                    {url ? (
                      <img
                        src={url}
                        alt={`Attachment ${index + 1}`}
                        className="w-full h-full object-cover absolute inset-0"
                        onError={(e) => {
                          const el = e.target as HTMLImageElement;
                          el.style.display = 'none';
                          const parent = el.closest('.group');
                          const fallback = parent?.querySelector('[data-thumb-fallback]') as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      data-thumb-fallback
                      className="w-full h-full flex items-center justify-center absolute inset-0"
                      style={{
                        color: mutedColor,
                        display: url ? 'none' : 'flex',
                        backgroundColor: `${borderColor}40`
                      }}
                    >
                      <FileImage className="w-8 h-8 opacity-50" />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removeImageAtIndex(index);
                      }}
                      className="absolute top-1 right-1 z-10 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold bg-black/50 hover:bg-black/70 transition-colors"
                      aria-label={`Remove image ${index + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image full size view"
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {onPreferredScheduleTextChange && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
            Preferred schedule
          </p>
          <div className="rounded-xl border p-5 space-y-2" style={{ borderColor, backgroundColor: cardSurface }}>
            <p className="text-sm" style={{ color: mutedColor }}>
              When would you prefer to have this service? (e.g. weekday mornings, after March 15, flexible)
            </p>
            <label htmlFor="quote-preferred-schedule" className="sr-only">
              Preferred schedule (optional)
            </label>
            <textarea
              id="quote-preferred-schedule"
              value={preferredScheduleText}
              onChange={(e) => onPreferredScheduleTextChange(e.target.value)}
              rows={3}
              className="w-full rounded-lg border p-3 text-sm resize-y"
              style={{ borderColor, color: textColor }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingStepQuote;
